import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { serializeEleve } from './serializer';
import { ELEVES_ACCOUNT_KEY } from '@teetsh/app/src/queries/eleves/useEleves';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';
import useNotification from 'containers/App/Notifications/useNotification';

function updateEleve(props: {
  schoolyearId: string;
  schoolId: string;
  eleve: Eleve;
  tz: string;
}) {
  const requestURL = `/schoolyears/${props.schoolyearId}/schools/${props.schoolId}/eleves/${props.eleve.id}`;
  const eleve = serializeEleve(props.eleve, props.tz);

  return request(requestURL, {
    method: 'PATCH',
    body: {
      id: eleve.id,
      classroomId: eleve.classroomId,
      givenName: eleve.givenName,
      familyName: eleve.familyName,
      groupName: eleve.groupName,
      picture: eleve.picture,
      ine: eleve.ine,
      arrivalDate: eleve.arrivalDate,
      radiationDate: eleve.radiationDate,
      gender: eleve.gender,
      birthday: eleve.birthday,
      decloisonement: eleve.decloisonement,
      comment: eleve.comment,
      customFields: eleve.customFields,
      groups: eleve.groups,
    },
  });
}

export const UPDATE_ELEVE_KEY = 'UPDATE_ELEVE';

export default function useUpdateEleve() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const user = useUser();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['eleve']);

  const { mutateAsync } = useMutation({
    mutationKey: [UPDATE_ELEVE_KEY],
    mutationFn: (eleve: Partial<Eleve>) => {
      const previousEleves = queryClient.getQueryData<Eleve[]>([
        ELEVES_ACCOUNT_KEY,
        schoolyear.id,
        school.id,
      ]);
      const previousEleve = (previousEleves || [])?.find(
        ({ id }) => id === eleve.id,
      );
      if (!previousEleve) {
        return Promise.reject();
      }

      return updateEleve({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        eleve: {
          ...previousEleve,
          ...eleve,
        },
        tz: user.location,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELEVES_ACCOUNT_KEY],
      });
    },

    onError: () => {
      notifyError({
        title: t('eleve:messages.updateError_one'),
      });
    },
  });
  return mutateAsync;
}

interface UpdateEleveCustomFieldProps {
  eleveId: string;
  customFieldId: string;
  value: string;
}

export const UPDATE_CUSTOM_FIELD_ELEVE_KEY = 'UPDATE_CUSTOM_FIELDS_ELEVE';

export function useUpdateEleveCustomField() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const user = useUser();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['eleve']);

  const { mutateAsync } = useMutation({
    mutationKey: [UPDATE_CUSTOM_FIELD_ELEVE_KEY],
    mutationFn: ({
      eleveId,
      customFieldId,
      value,
    }: UpdateEleveCustomFieldProps) => {
      const previousEleves = queryClient.getQueryData<Eleve[]>([
        ELEVES_ACCOUNT_KEY,
        schoolyear.id,
        school.id,
      ]);
      const previousEleve = (previousEleves || [])?.find(
        ({ id }) => id === eleveId,
      );
      if (!previousEleve) {
        return Promise.reject();
      }
      let updatedCustomFields = previousEleve.customFields || [];
      const matchingCustomField = updatedCustomFields.find(
        customField => customField.id === customFieldId,
      );
      if (!matchingCustomField) {
        updatedCustomFields.push({
          id: customFieldId,
          value,
        });
      } else if (value === undefined) {
        updatedCustomFields = updatedCustomFields.filter(
          customField => customField.id !== customFieldId,
        );
      } else {
        updatedCustomFields = updatedCustomFields.map(customField => {
          if (customField.id === customFieldId) {
            return {
              ...customField,
              value,
            };
          }
          return customField;
        });
      }

      return updateEleve({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        eleve: {
          ...previousEleve,
          customFields: updatedCustomFields,
        },
        tz: user.location,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELEVES_ACCOUNT_KEY],
      });
    },

    onError: () => {
      notifyError({
        title: t('eleve:messages.updateError_one'),
      });
    },
  });
  return mutateAsync;
}
